import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CustomersPage } from './pages/CustomersPage';
import { LLMProvidersPage } from './pages/LLMProvidersPage';
import { LLMModelsPage } from './pages/LLMModelsPage';
import { PromptTemplatesPage } from './pages/PromptTemplatesPage';
import { ContactReasonsPage } from './pages/ContactReasonsPage';
import { ToolDefinitionsPage } from './pages/ToolDefinitionsPage';
import { HookProvidersPage } from './pages/HookProvidersPage';
import { SubscriptionPlansPage } from './pages/SubscriptionPlansPage';
import { SpeechToTextPage } from './pages/SpeechToTextPage';
import { SttModelsPage } from './pages/SttModelsPage';
import { SttModelDetailPage } from './pages/SttModelDetailPage';
import { TextToSpeechPage } from './pages/TextToSpeechPage';
import { OverviewPage } from './pages/OverviewPage';
import { RegistrationProfilesPage } from './pages/RegistrationProfilesPage';
import { PendingRegistrationsPage } from './pages/PendingRegistrationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { SummaryPromptCollectionsPage } from './pages/SummaryPromptCollectionsPage';
import { SystemGuidelinesPage } from './pages/SystemGuidelinesPage';
import { StandardEvaluationCriteriaPage } from './pages/StandardEvaluationCriteriaPage';
import { WebhookTemplatesPage } from './pages/WebhookTemplatesPage';
import { FillerSettingsPage } from './pages/FillerSettingsPage';
import { BackgroundJobLlmSettingsPage } from './pages/BackgroundJobLlmSettingsPage';
import { BulkModelActivationPage } from './pages/BulkModelActivationPage';
import { RateLimitSettingsPage } from './pages/RateLimitSettingsPage';
import { RateLimitLogsPage } from './pages/RateLimitLogsPage';
import { CustomerAgentConfigPage } from './pages/CustomerAgentConfigPage';
import { NotificationTemplatesPage } from './pages/NotificationTemplatesPage';
import { BillingPage } from './pages/BillingPage';
import { CostOverviewPage } from './pages/CostOverviewPage';
import { CostSettingsPage } from './pages/CostSettingsPage';
import { CallVolumePage } from './pages/call-volume/CallVolumePage';
import { SystemEventsPage } from './pages/SystemEventsPage';
import { SystemEventLogPage } from './pages/SystemEventLogPage';
import { McpServersPage } from './pages/McpServersPage';
import { IntegrationsListPage } from './pages/integrations/IntegrationsListPage';
import { IntegrationDetailPage } from './pages/integrations/IntegrationDetailPage';
import { CuratedNotificationsPage } from './pages/CuratedNotificationsPage';
import { ConditionSuggestionsPage } from './pages/condition-suggestions/ConditionSuggestionsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { BlockedPhoneNumbersPage } from './pages/BlockedPhoneNumbersPage';
import { CidPoolPage } from './pages/CidPoolPage';
import { TenantsPage } from './pages/TenantsPage';
import { TenantAssignmentsPage } from './pages/TenantAssignmentsPage';
import { TenantFilterProvider } from '../contexts/TenantFilterContext';
import { ErrorEventsPage } from './pages/error-events/ErrorEventsPage';
import { RequireSuperAdmin } from './RequireSuperAdmin';

interface User {
  id: string;
  email: string;
  name?: string;
  /** Admin role from login response — drives sidebar/route access. */
  role?: string;
  isAuthenticated: boolean;
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
  onUserUpdate: (updatedUser: User) => void;
}

export type PageType =
  | 'overview'
  | 'customers'
  | 'llm-providers'
  | 'llm-models'
  | 'prompt-templates'
  | 'contact-reasons'
  | 'summary-collections'
  | 'system-guidelines'
  | 'evaluation-criteria'
  | 'filler-settings'
  | 'background-job-llms'
  | 'rate-limit-settings'
  | 'rate-limit-logs'
  | 'tool-definitions'
  | 'hook-providers'
  | 'webhook-templates'
  | 'subscription-plans'
  | 'billing'
  | 'cost-overview'
  | 'cost-settings'
  | 'call-volume'
  | 'speech-to-text'
  | 'text-to-speech'
  | 'registration-profiles'
  | 'pending-registrations'
  | 'notification-templates'
  | 'system-events'
  | 'system-event-log'
  | 'mcp-servers'
  | 'integrations'
  | 'curated-notifications'
  | 'admin-users'
  | 'condition-suggestions'
  | 'bulk-model-activation'
  | 'blocked-phone-numbers'
  | 'cid-pool'
  | 'tenants'
  | 'tenant-assignments'
  | 'error-events'
  | 'profile';

export function Dashboard({ user, onLogout, onUserUpdate }: DashboardProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const getCurrentPageFromPath = (): PageType => {
    const path = location.pathname;
    if (path === '/' || path === '') return 'overview';
    if (path === '/customers') return 'customers';
    if (path === '/llm-providers') return 'llm-providers';
    if (path === '/llm-models') return 'llm-models';
    if (path === '/prompt-templates') return 'prompt-templates';
    if (path === '/contact-reasons') return 'contact-reasons';
    if (path === '/summary-collections') return 'summary-collections';
    if (path === '/system-guidelines') return 'system-guidelines';
    if (path === '/evaluation-criteria') return 'evaluation-criteria';
    if (path === '/filler-settings') return 'filler-settings';
    if (path === '/background-job-llms') return 'background-job-llms';
    if (path === '/rate-limit-settings') return 'rate-limit-settings';
    if (path === '/rate-limit-logs') return 'rate-limit-logs';
    if (path === '/tool-definitions') return 'tool-definitions';
    if (path === '/hook-providers') return 'hook-providers';
    if (path === '/webhook-templates') return 'webhook-templates';
    if (path === '/subscription-plans') return 'subscription-plans';
    if (path === '/billing') return 'billing';
    if (path === '/cost-overview') return 'cost-overview';
    if (path === '/cost-settings') return 'cost-settings';
    if (path === '/call-volume') return 'call-volume';
    if (path.startsWith('/speech-to-text')) return 'speech-to-text';
    if (path === '/text-to-speech') return 'text-to-speech';
    if (path === '/registration-profiles') return 'registration-profiles';
    if (path === '/pending-registrations') return 'pending-registrations';
    if (path === '/notification-templates') return 'notification-templates';
    if (path === '/system-events') return 'system-events';
    if (path === '/system-event-log') return 'system-event-log';
    if (path === '/mcp-servers') return 'mcp-servers';
    if (path.startsWith('/integrations')) return 'integrations';
    if (path === '/curated-notifications') return 'curated-notifications';
    if (path === '/condition-suggestions') return 'condition-suggestions';
    if (path === '/admin-users') return 'admin-users';
    if (path === '/bulk-model-activation') return 'bulk-model-activation';
    if (path === '/blocked-phone-numbers') return 'blocked-phone-numbers';
    if (path === '/cid-pool') return 'cid-pool';
    if (path === '/tenants') return 'tenants';
    if (path === '/tenant-assignments') return 'tenant-assignments';
    if (path === '/error-events') return 'error-events';
    if (path === '/profile') return 'profile';
    return 'overview';
  };

  const currentPage = getCurrentPageFromPath();

  const handlePageChange = (page: PageType) => {
    const pathMap = {
      'overview': '/',
      'customers': '/customers',
      'llm-providers': '/llm-providers',
      'llm-models': '/llm-models',
      'prompt-templates': '/prompt-templates',
      'contact-reasons': '/contact-reasons',
      'summary-collections': '/summary-collections',
      'system-guidelines': '/system-guidelines',
      'evaluation-criteria': '/evaluation-criteria',
      'filler-settings': '/filler-settings',
      'background-job-llms': '/background-job-llms',
      'rate-limit-settings': '/rate-limit-settings',
      'rate-limit-logs': '/rate-limit-logs',
      'tool-definitions': '/tool-definitions',
      'hook-providers': '/hook-providers',
      'webhook-templates': '/webhook-templates',
      'subscription-plans': '/subscription-plans',
      'billing': '/billing',
      'cost-overview': '/cost-overview',
      'cost-settings': '/cost-settings',
      'call-volume': '/call-volume',
      'speech-to-text': '/speech-to-text',
      'text-to-speech': '/text-to-speech',
      'registration-profiles': '/registration-profiles',
      'pending-registrations': '/pending-registrations',
      'notification-templates': '/notification-templates',
      'system-events': '/system-events',
      'system-event-log': '/system-event-log',
      'mcp-servers': '/mcp-servers',
      'integrations': '/integrations',
      'curated-notifications': '/curated-notifications',
      'admin-users': '/admin-users',
      'condition-suggestions': '/condition-suggestions',
      'bulk-model-activation': '/bulk-model-activation',
      'blocked-phone-numbers': '/blocked-phone-numbers',
      'cid-pool': '/cid-pool',
      'tenants': '/tenants',
      'tenant-assignments': '/tenant-assignments',
      'error-events': '/error-events',
      'profile': '/profile'
    };
    navigate(pathMap[page]);
  };

  return (
    <TenantFilterProvider enabled={user.role === 'super_admin'}>
    <div className="flex h-screen bg-gray-100">
      <Sidebar currentPage={currentPage} onPageChange={handlePageChange} userRole={user.role} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header user={user} onLogout={onLogout} onNavigateToProfile={() => handlePageChange('profile')} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-6 py-8">
            <Routes>
              {/* Open to every authenticated admin (Portal Admin allowlist + Super Admin). */}
              <Route path="/" element={<OverviewPage userRole={user.role} />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:customerId/agent-config" element={<CustomerAgentConfigPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/cost-overview" element={<CostOverviewPage />} />
              <Route path="/call-volume" element={<CallVolumePage />} />
              <Route path="/pending-registrations" element={<PendingRegistrationsPage />} />
              <Route path="/profile" element={<ProfilePage user={user} onUserUpdate={onUserUpdate} />} />

              {/* Super-Admin-only — non-super-admins are redirected to "/". */}
              <Route path="/llm-providers" element={<RequireSuperAdmin role={user.role}><LLMProvidersPage /></RequireSuperAdmin>} />
              <Route path="/llm-models" element={<RequireSuperAdmin role={user.role}><LLMModelsPage /></RequireSuperAdmin>} />
              <Route path="/prompt-templates" element={<RequireSuperAdmin role={user.role}><PromptTemplatesPage /></RequireSuperAdmin>} />
              <Route path="/contact-reasons" element={<RequireSuperAdmin role={user.role}><ContactReasonsPage /></RequireSuperAdmin>} />
              <Route path="/summary-collections" element={<RequireSuperAdmin role={user.role}><SummaryPromptCollectionsPage /></RequireSuperAdmin>} />
              <Route path="/system-guidelines" element={<RequireSuperAdmin role={user.role}><SystemGuidelinesPage /></RequireSuperAdmin>} />
              <Route path="/evaluation-criteria" element={<RequireSuperAdmin role={user.role}><StandardEvaluationCriteriaPage /></RequireSuperAdmin>} />
              <Route path="/filler-settings" element={<RequireSuperAdmin role={user.role}><FillerSettingsPage /></RequireSuperAdmin>} />
              <Route path="/background-job-llms" element={<RequireSuperAdmin role={user.role}><BackgroundJobLlmSettingsPage /></RequireSuperAdmin>} />
              <Route path="/rate-limit-settings" element={<RequireSuperAdmin role={user.role}><RateLimitSettingsPage /></RequireSuperAdmin>} />
              <Route path="/cost-settings" element={<RequireSuperAdmin role={user.role}><CostSettingsPage /></RequireSuperAdmin>} />
              <Route path="/rate-limit-logs" element={<RequireSuperAdmin role={user.role}><RateLimitLogsPage /></RequireSuperAdmin>} />
              <Route path="/tool-definitions" element={<RequireSuperAdmin role={user.role}><ToolDefinitionsPage /></RequireSuperAdmin>} />
              <Route path="/hook-providers" element={<RequireSuperAdmin role={user.role}><HookProvidersPage /></RequireSuperAdmin>} />
              <Route path="/webhook-templates" element={<RequireSuperAdmin role={user.role}><WebhookTemplatesPage /></RequireSuperAdmin>} />
              <Route path="/subscription-plans" element={<RequireSuperAdmin role={user.role}><SubscriptionPlansPage /></RequireSuperAdmin>} />
              <Route path="/speech-to-text" element={<RequireSuperAdmin role={user.role}><SpeechToTextPage /></RequireSuperAdmin>} />
              <Route path="/speech-to-text/:providerId/models" element={<RequireSuperAdmin role={user.role}><SttModelsPage /></RequireSuperAdmin>} />
              <Route path="/speech-to-text/:providerId/models/:modelName" element={<RequireSuperAdmin role={user.role}><SttModelDetailPage /></RequireSuperAdmin>} />
              <Route path="/text-to-speech" element={<RequireSuperAdmin role={user.role}><TextToSpeechPage /></RequireSuperAdmin>} />
              <Route path="/registration-profiles" element={<RequireSuperAdmin role={user.role}><RegistrationProfilesPage /></RequireSuperAdmin>} />
              <Route path="/notification-templates" element={<RequireSuperAdmin role={user.role}><NotificationTemplatesPage /></RequireSuperAdmin>} />
              <Route path="/system-events" element={<RequireSuperAdmin role={user.role}><SystemEventsPage /></RequireSuperAdmin>} />
              <Route path="/system-event-log" element={<RequireSuperAdmin role={user.role}><SystemEventLogPage /></RequireSuperAdmin>} />
              <Route path="/mcp-servers" element={<RequireSuperAdmin role={user.role}><McpServersPage /></RequireSuperAdmin>} />
              <Route path="/integrations" element={<RequireSuperAdmin role={user.role}><IntegrationsListPage /></RequireSuperAdmin>} />
              <Route path="/integrations/:id" element={<RequireSuperAdmin role={user.role}><IntegrationDetailPage /></RequireSuperAdmin>} />
              <Route path="/curated-notifications" element={<RequireSuperAdmin role={user.role}><CuratedNotificationsPage /></RequireSuperAdmin>} />
              <Route path="/condition-suggestions" element={<RequireSuperAdmin role={user.role}><ConditionSuggestionsPage /></RequireSuperAdmin>} />
              <Route path="/admin-users" element={<RequireSuperAdmin role={user.role}><AdminUsersPage currentUserId={user.id} /></RequireSuperAdmin>} />
              <Route path="/bulk-model-activation" element={<RequireSuperAdmin role={user.role}><BulkModelActivationPage /></RequireSuperAdmin>} />
              <Route path="/blocked-phone-numbers" element={<RequireSuperAdmin role={user.role}><BlockedPhoneNumbersPage /></RequireSuperAdmin>} />
              <Route path="/cid-pool" element={<RequireSuperAdmin role={user.role}><CidPoolPage /></RequireSuperAdmin>} />
              <Route path="/tenants" element={<RequireSuperAdmin role={user.role}><TenantsPage /></RequireSuperAdmin>} />
              <Route path="/tenant-assignments" element={<RequireSuperAdmin role={user.role}><TenantAssignmentsPage /></RequireSuperAdmin>} />
              <Route path="/error-events" element={<RequireSuperAdmin role={user.role}><ErrorEventsPage /></RequireSuperAdmin>} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
    </TenantFilterProvider>
  );
}
